import { SearchInput } from '@integration-app/ui/Input'
import Select from '@integration-app/ui/Select'
import clsx from 'clsx'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { InnerPageLayout } from 'routes/layouts'

import { HeadMetatags } from 'routes/components/HeadMetatags'
import { Pagination } from './components/Pagination'
import { DOCS_LINK, LINKS } from 'routes/constants'
import { AvailableConnectors } from './components/AvailableConnectors'
import classes from './connectors.module.css'

export type CategoryType = {
  name: string
  iconSrc: string
  categories: string[]
  status: 'production' | 'planned' | 'legacy'
}

export default function ConnectorsPage({
  connectors,
}: {
  connectors: CategoryType[]
}) {
  const { query, push } = useRouter()

  function updateQuery(modifiedQuery: Record<string, string>) {
    // @ts-expect-error TS(2345): Argument of type '{ [x: string]: string | string[]... Remove this comment to see the full error message
    const nonEmptyQueryParams = removeEmptyQueries({
      ...query,
      ...modifiedQuery,
    })

    void push(
      {
        query: nonEmptyQueryParams,
      },
      undefined,
      {
        shallow: true,
      },
    )
  }

  function handleSearchChange(search: string) {
    void updateQuery({
      search,
    })
  }

  function handleCategorySelect(category: string) {
    void updateQuery({
      category,
    })
  }

  const actualConnectors = connectors.filter(
    (connector) => connector.status !== 'legacy',
  )

  return (
    <ConnectorsPageWithQuery
      connectors={actualConnectors}
      search={query?.search as string}
      onSearch={handleSearchChange}
      onCategorySelect={handleCategorySelect}
      selectedCategory={query?.category as string}
    />
  )
}

function ConnectorsPageWithQuery({
  connectors,
  selectedCategory,
  onCategorySelect,
  search = '',
  onSearch,
}: {
  connectors: CategoryType[]
  selectedCategory?: string
  onCategorySelect: (category: string) => void
  search?: string
  onSearch: (search: string) => void
}) {
  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    onSearch(e.target.value)
  }

  function handleCategorySelect(e) {
    onCategorySelect(e.target.value)
  }

  const connectorsByCategory = getConnectorsByCategory(
    connectors,
    selectedCategory,
  )

  const connectorsBySearch = search
    ? getConnectorsBySearch(connectorsByCategory, search)
    : connectorsByCategory

  const categories = orderCategories(
    getUniqueCategoriesFromConnectors(
      connectors,
      search ? connectorsBySearch : connectors,
    ),
  )
  const connectorsByStatus = splitArrayByStatus(connectorsBySearch)

  const allConnectorsAmount = getConnectorsBySearch(connectors, search).length

  return (
    <InnerPageLayout>
      <HeadMetatags title='Connectors'>
        <link
          key='canonical'
          rel='canonical'
          href={`${process.env.NEXT_PUBLIC_SITE_URI}${LINKS.CONNECTORS}`}
        />
      </HeadMetatags>
      <div className={classes.content}>
        <aside className={classes.connectorsSidebar}>
          <h1 className={clsx('likeH2', classes.header)}>Connectors</h1>
          <div className={classes.text}>
            <p>
              Connections are used to access external applications on behalf of
              your users. See the full list of apps you can integrate with using
              our platform.
            </p>
          </div>
          <Select
            onChange={handleCategorySelect}
            className={classes.connectorsTypeSelect}
            value={selectedCategory}
          >
            <option value={''}>All</option>
            {categories.map(({ name, amount }) => (
              <option key={name} value={name}>
                {name} ({amount})
              </option>
            ))}
          </Select>
          <div className={classes.connectorsTypeSearchAndSelect}>
            <SearchInput
              placeholder={'Type to search Connector'}
              className={classes.input}
              value={search}
              variant={'border'}
              onChange={handleSearchChange}
            />
          </div>
          <div className={classes.connectorsTypeList}>
            <ul>
              <ConnectorListItem
                // @ts-expect-error TS(2322): Type 'undefined' is not assignable to type 'string... Remove this comment to see the full error message
                category={undefined}
                selectedCategory={selectedCategory}
                title={`All`}
                amount={allConnectorsAmount}
                search={search}
              />
              {categories.map(({ name, amount }) => (
                <ConnectorListItem
                  key={name}
                  category={name}
                  title={`${name}`}
                  amount={amount}
                  selectedCategory={selectedCategory}
                  search={search}
                />
              ))}
            </ul>
          </div>
        </aside>
        <div className={classes.connectorsWrapper}>
          {connectorsBySearch.length === 0 && (
            <NothingFound search={search} category={selectedCategory} />
          )}
          <AvailableConnectors connectors={connectorsByStatus.production} />
          <Pagination />
          <CustomConnectors />
        </div>
      </div>
    </InnerPageLayout>
  )
}

function CustomConnectors() {
  return (
    <div className={classes.customWrapper}>
      <h2 className={clsx('likeH3', classes.customConnectorsTitle)}>
        Do not see the app you need?
      </h2>
      <p className={classes.customConenectorParagraph}>
        You can either build one yourself using our{' '}
        <a target='_blank' href={`${DOCS_LINK}/connectors/building-connectors`}>
          Connector Builder
        </a>{' '}
        or request a custom connector from our team.
      </p>
      <div className={classes.customButtons}>
        <a
          href={'mailto:hello@integration.app?subject=I want a connector'}
          className={'button blue size-l button__rounded'}
          target={'_blank'}
        >
          Request a Connector
        </a>
        <a
          href={`${DOCS_LINK}/connectors/building-connectors`}
          className={'button size-l secondary button__rounded'}
          target={'_blank'}
        >
          Build Custom Connector
        </a>
      </div>
    </div>
  )
}

function ConnectorListItem({
  title,
  category,
  amount,
  selectedCategory,
  search,
}: {
  title: string
  category: string
  amount: number
  selectedCategory?: string
  search?: string
}) {
  return (
    <li className={classes.connectorsTypeList_listItem}>
      <Link
        href={{
          query: removeEmptyQueries({
            category: category,
            // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
            search: search,
          }),
        }}
        passHref
        shallow
      >
        <a
          href=''
          className={clsx(
            category === selectedCategory &&
              classes.connectorsTypeList_link__active,
            classes.connectorsTypeList_link,
          )}
        >
          {title} <span className={classes.listItemLength}>{amount}</span>
        </a>
      </Link>
    </li>
  )
}

function NothingFound({ search, category }) {
  const searchMessage = `Sorry, we couldn't find any results${
    search ? ` for your search query “${search}”` : ''
  }${category ? ` in category “${category}”` : ''}.`
  return (
    <div>
      <h2>{searchMessage}</h2>
      <p>Browse our categories or sections to find what you're looking for. </p>
    </div>
  )
}

function splitArrayByStatus(
  arr: CategoryType[],
): Record<CategoryType['status'], CategoryType[]> {
  const result = {} as Record<CategoryType['status'], CategoryType[]>

  // Loop through the input array
  arr.forEach((obj) => {
    const { status } = obj
    // Check if the status key already exists in the result object
    if (status in result) {
      // If yes, push the object into the existing array
      result[status].push(obj)
    } else {
      // If no, create a new array for the status key and push the object into it
      result[status] = [obj]
    }
  })

  return result
}

function getUniqueCategoriesFromConnectors(
  connectors: CategoryType[],
  filteredConnectors: CategoryType[],
) {
  const uniqueCategories = [
    ...new Set(connectors.map((connector) => connector.categories).flat()),
  ].filter((x) => {
    // Some connectors have empty categories
    // We do not want to show empty category in the list
    return !!x
  })

  return uniqueCategories.map((x) => ({
    name: x,
    amount: getAmountOfConnectorsByCategory(filteredConnectors, x),
  }))
}

function getAmountOfConnectorsByCategory(
  connectors: CategoryType[],
  category: string,
) {
  return connectors.filter((connector) =>
    connector.categories.includes(category),
  ).length
}

const CATEGORIES_ORDER = [
  'CRM',
  'Marketing Automation',
  'Task Management',
  'Accounting',
  'HR',
]

function orderCategories(categories: Array<{ name: string; amount: number }>) {
  return categories.sort((a, b) => {
    const indexA = CATEGORIES_ORDER.indexOf(a.name)
    const indexB = CATEGORIES_ORDER.indexOf(b.name)
    if (indexA === indexB) return 0
    if (indexA === -1) return 1
    if (indexB === -1) return -1
    return indexA < indexB ? -1 : 1
  })
}

function getConnectorsByCategory(
  categories: CategoryType[],
  category?: string,
) {
  if (!category) return categories
  return categories.filter((connector) =>
    connector.categories.includes(category),
  )
}

function getConnectorsBySearch(categories: CategoryType[], search?: string) {
  if (!search) return categories
  const searchInLowerCase = search.toLowerCase()

  return categories.filter((connector) =>
    connector.name.toLowerCase().includes(searchInLowerCase),
  )
}

function removeEmptyQueries(queries: Record<string, string | string[]>) {
  return Object.fromEntries(
    Object.entries(queries).filter(
      ([_, v]) => v !== '' && v !== undefined && v !== null,
    ),
  )
}
